/**
 * QuickReportModal — Unified report system with type selector.
 * Supports create and edit modes. Field visibility and requirements
 * are driven dynamically by fieldOverrides from the Report Builder config.
 *
 * Batch mode: In create mode, users can add multiple reports to a batch
 * and submit them all at once (shopping-cart UX). Edit mode stays single-report.
 */

import { useState, useMemo, useEffect, useRef, useReducer, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { AnimatePresence } from 'motion/react';
import {
  CalendarIcon,
  ClockIcon,
  FileTextIcon,
  Building2Icon,
  HashIcon,
  HardHat,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { FormModal, FormField, useVinylSubmit, MODAL_COLORS, triggerHapticFeedback } from '@/components/modal';
import { useMyHospitals } from '@/features/hospitals';
import { useEmploymentRole } from '@/features/auth';
import { useCreateReport, useUpdateReport, useTreatmentTypes, useHelmetTypes } from '../hooks';
import { localizedName } from '@/utils/localized';
import { getQuantityLabel } from '@/utils/quantityLabel';
import { parseFieldOverrides } from '@/features/admin/utils/reportFieldCatalog';
import { BatchItemCard } from './BatchItemCard';
import { BatchFooter } from './BatchFooter';
import type { BatchItem } from './BatchItemCard';
import type { Report, ReportFormData, ReportTypeCode } from '../types';

function generateTimeSlots(): string[] {
  const slots: string[] = [];
  for (let h = 6; h <= 22; h++) {
    slots.push(`${String(h).padStart(2, '0')}:00`);
    slots.push(`${String(h).padStart(2, '0')}:30`);
  }
  return slots;
}

const TIME_SLOTS = generateTimeSlots();

// ============================================
// Batch Reducer
// ============================================

type BatchMode = 'adding' | 'editing';

interface BatchState {
  items: BatchItem[];
  mode: BatchMode;
  editingIndex: number | null;
}

type BatchAction =
  | { type: 'ADD_ITEM'; item: BatchItem }
  | { type: 'START_EDIT'; index: number }
  | { type: 'UPDATE_ITEM'; index: number; item: BatchItem }
  | { type: 'CANCEL_EDIT' }
  | { type: 'REMOVE_ITEM'; index: number }
  | { type: 'RESET' };

const INITIAL_BATCH_STATE: BatchState = {
  items: [],
  mode: 'adding',
  editingIndex: null,
};

function batchReducer(state: BatchState, action: BatchAction): BatchState {
  switch (action.type) {
    case 'ADD_ITEM':
      return { ...state, items: [...state.items, action.item], mode: 'adding', editingIndex: null };
    case 'START_EDIT':
      return { ...state, mode: 'editing', editingIndex: action.index };
    case 'UPDATE_ITEM':
      return {
        ...state,
        items: state.items.map((item, i) => (i === action.index ? action.item : item)),
        mode: 'adding',
        editingIndex: null,
      };
    case 'CANCEL_EDIT':
      return { ...state, mode: 'adding', editingIndex: null };
    case 'REMOVE_ITEM':
      return {
        ...state,
        items: state.items.filter((_, i) => i !== action.index),
        // If we were editing the removed item, cancel editing
        mode: state.editingIndex === action.index ? 'adding' : state.mode,
        editingIndex: state.editingIndex === action.index ? null : state.editingIndex,
      };
    case 'RESET':
      return INITIAL_BATCH_STATE;
    default:
      return state;
  }
}

// ============================================
// Component
// ============================================

interface QuickReportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  existingReport?: Report;
  defaultDate?: string;
}

export function QuickReportModal({
  isOpen,
  onClose,
  onSuccess,
  existingReport,
  defaultDate,
}: QuickReportModalProps) {
  const { t, i18n } = useTranslation();
  const isHebrew = i18n.language === 'he';
  const isRTL = typeof document !== 'undefined' ? document.documentElement.dir === 'rtl' : false;
  const isEditMode = !!existingReport;
  const isBatchMode = !isEditMode;
  const modalContentRef = useRef<HTMLDivElement>(null);
  const batchListRef = useRef<HTMLDivElement>(null);
  const { isAnimating, submitWithAnimation, submitBatchWithAnimation } = useVinylSubmit({
    modalContentRef,
    vinylLabel: 'REPORT',
    notifyReportSubmitted: true,
  });

  // Batch state
  const [batch, dispatch] = useReducer(batchReducer, INITIAL_BATCH_STATE);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Auto-scroll batch list to bottom when items are added
  useEffect(() => {
    if (batch.items.length > 0 && batchListRef.current) {
      requestAnimationFrame(() => {
        batchListRef.current?.scrollTo({ top: batchListRef.current.scrollHeight, behavior: 'smooth' });
      });
    }
  }, [batch.items.length]);

  // Employment role — dynamic report types + default
  const { assignedReportTypes, defaultReportTypeCode } = useEmploymentRole();

  // Data hooks — useMyHospitals returns sorted by isPrimary desc (primary first)
  const { data: myHospitals, isLoading: hospitalsLoading } = useMyHospitals();
  const hospitals = myHospitals ?? [];
  const { data: treatmentTypesData, isLoading: treatmentTypesLoading } = useTreatmentTypes({ isActive: 'true', limit: 100 });
  const treatmentTypes = treatmentTypesData?.treatmentTypes ?? [];
  const { data: helmetTypesData } = useHelmetTypes({ isActive: 'true', limit: 100 });
  const helmetTypes = helmetTypesData?.helmetTypes ?? [];
  const createMutation = useCreateReport();
  const updateMutation = useUpdateReport();

  // Available report types come from the user's employment role
  const availableReportTypes = assignedReportTypes;

  // Form state — restrict to current month only
  const today = useMemo(() => new Date().toISOString().split('T')[0], []);
  const currentMonthMin = useMemo(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`;
  }, []);
  const currentMonthMax = useMemo(() => {
    const now = new Date();
    const lastDay = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
  }, []);
  const [reportTypeCode, setReportTypeCode] = useState<ReportTypeCode>('TREATMENT');
  const [reportDate, setReportDate] = useState<string>(today);
  const [selectedHospitalId, setSelectedHospitalId] = useState<string>('');
  const [treatmentType, setTreatmentType] = useState<string>('');
  const [helmetType, setHelmetType] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [notes, setNotes] = useState<string>('');
  const [quantity, setQuantity] = useState<number>(1);
  const [error, setError] = useState<string>('');

  // Dynamic field config from the active report type's fieldOverrides
  const activeRT = useMemo(
    () => availableReportTypes.find((rt) => rt.code === reportTypeCode),
    [availableReportTypes, reportTypeCode],
  );
  const fieldConfig = useMemo(
    () => parseFieldOverrides(activeRT?.fieldOverrides ?? null),
    [activeRT?.fieldOverrides],
  );
  const isVisible = (key: string) => fieldConfig[key]?.visible ?? true;
  const isRequired = (key: string) => fieldConfig[key]?.required ?? false;

  // Filter helmets based on selected treatment type (with fallback to all)
  const filteredHelmetTypes = useMemo(() => {
    if (!treatmentType || !helmetTypes) return helmetTypes;
    const selectedTT = treatmentTypes?.find(tt => tt.code === treatmentType);
    if (!selectedTT?.compatibleHelmetCodes?.length) return helmetTypes; // fallback: show all
    return helmetTypes.filter(ht => selectedTT.compatibleHelmetCodes!.includes(ht.code));
  }, [treatmentType, treatmentTypes, helmetTypes]);

  // Determine if selected treatment has defined helmet connections
  const selectedTreatmentHasConnections = useMemo(() => {
    if (!treatmentType) return false;
    const selectedTT = treatmentTypes?.find(tt => tt.code === treatmentType);
    return !!selectedTT?.compatibleHelmetCodes?.length;
  }, [treatmentType, treatmentTypes]);

  // Modal open / edit init
  useEffect(() => {
    if (isOpen) {
      dispatch({ type: 'RESET' });
      setIsSubmitting(false);
      if (existingReport) {
        setReportTypeCode(existingReport.reportTypeCode);
        setReportDate(existingReport.date);
        setSelectedHospitalId(existingReport.hospitalId);
        setTreatmentType(existingReport.treatmentType || '');
        setHelmetType(existingReport.helmetType || '');
        setStartTime(existingReport.startTime);
        setEndTime(existingReport.endTime);
        setNotes(existingReport.notes || '');
        setQuantity(existingReport.quantity ?? 1);
      } else {
        // Auto-select: if 1 report type → use it; if multiple → prefer configured default, else first
        const defaultCode = availableReportTypes.length === 1
          ? availableReportTypes[0].code
          : (availableReportTypes.find((rt) => rt.code === defaultReportTypeCode)?.code ?? availableReportTypes[0]?.code ?? 'TREATMENT');
        setReportTypeCode(defaultCode);
        setReportDate(defaultDate ?? today);
        // Auto-select primary hospital (first in list, sorted by isPrimary desc)
        setSelectedHospitalId(hospitals.length > 0 ? hospitals[0].id : '');
        setTreatmentType('');
        setHelmetType('');
        setStartTime('');
        setEndTime('');
        setNotes('');
        setQuantity(1);
      }
      setError('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, existingReport]);

  // Auto-select primary hospital when hospitals load (create mode only)
  useEffect(() => {
    if (!isOpen || isEditMode) return;
    if (hospitals.length > 0 && !selectedHospitalId) {
      setSelectedHospitalId(hospitals[0].id);
    }
  }, [hospitals, selectedHospitalId, isOpen, isEditMode]);

  // Clear fields that become invisible when report type changes
  useEffect(() => {
    if (!isVisible('treatmentType')) setTreatmentType('');
    if (!isVisible('helmetType')) setHelmetType('');
    if (!isVisible('quantity')) setQuantity(1);
    if (!isVisible('startTime')) setStartTime('');
    if (!isVisible('endTime')) setEndTime('');
    if (!isVisible('notes')) setNotes('');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reportTypeCode]);

  // Auto-select helmet when exactly 1 compatible, reset if incompatible
  useEffect(() => {
    if (!treatmentType || !filteredHelmetTypes) return;

    if (selectedTreatmentHasConnections && filteredHelmetTypes.length === 1) {
      // Auto-select the only compatible helmet
      setHelmetType(filteredHelmetTypes[0].code);
    } else if (helmetType) {
      // Reset if current selection is not in filtered list
      const isCompatible = filteredHelmetTypes.some(ht => ht.code === helmetType);
      if (!isCompatible) setHelmetType('');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentType, filteredHelmetTypes, selectedTreatmentHasConnections]);

  const focusField = (id: string) => {
    requestAnimationFrame(() => {
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.focus({ preventScroll: true });
      }
    });
  };

  // Field value map for dynamic validation
  const fieldValues: Record<string, any> = {
    hospital: selectedHospitalId,
    date: reportDate,
    treatmentType,
    helmetType,
    quantity,
    startTime,
    endTime,
    notes,
  };

  const fieldFocusIds: Record<string, string> = {
    hospital: 'qr-hospital',
    date: 'qr-date',
    treatmentType: 'qr-treatment',
    helmetType: 'qr-helmet',
    quantity: 'qr-quantity',
    startTime: 'qr-start',
    endTime: 'qr-end',
    notes: 'qr-notes',
  };

  const validateForm = (): boolean => {
    setError('');

    // Check all required + visible fields
    for (const [key, cfg] of Object.entries(fieldConfig)) {
      if (cfg.visible && cfg.required && !fieldValues[key]) {
        const fieldLabel = t(`reportBuilder.fields.${key}`);
        setError(isHebrew ? `${fieldLabel} הוא שדה חובה` : `${fieldLabel} is required`);
        focusField(fieldFocusIds[key] || '');
        return false;
      }
    }

    // Special: if helmetType is visible and treatmentType has a value, require helmet
    if (isVisible('helmetType') && treatmentType && !helmetType) {
      setError(isHebrew ? 'נא לבחור סוג קסדה' : 'Please select a helmet type');
      focusField('qr-helmet');
      return false;
    }

    // Time ordering validation
    if (startTime && endTime && startTime >= endTime) {
      setError(isHebrew ? 'שעת סיום חייבת להיות אחרי שעת ההתחלה' : 'End time must be after start time');
      focusField('qr-end');
      return false;
    }

    return true;
  };

  const isFormValid = useMemo(() => {
    // Check required visible fields
    for (const [key, cfg] of Object.entries(fieldConfig)) {
      if (cfg.visible && cfg.required && !fieldValues[key]) return false;
    }
    // Special helmet check
    if (isVisible('helmetType') && treatmentType && !helmetType) return false;
    // Time ordering
    if (startTime && endTime && startTime >= endTime) return false;
    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fieldConfig, selectedHospitalId, reportDate, treatmentType, helmetType, quantity, startTime, endTime, notes]);

  // ============================================
  // Batch helpers
  // ============================================

  const snapshotForm = useCallback((): ReportFormData => ({
    reportTypeCode,
    hospitalId: selectedHospitalId,
    date: reportDate,
    startTime: isVisible('startTime') ? startTime : '',
    endTime: isVisible('endTime') ? endTime : '',
    notes: isVisible('notes') ? notes.trim() : '',
    quantity: isVisible('quantity') ? quantity : 1,
    status: 'SUBMITTED',
    ...(isVisible('treatmentType') && treatmentType ? { treatmentType } : {}),
    ...(isVisible('helmetType') && helmetType ? { helmetType } : {}),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [reportTypeCode, selectedHospitalId, reportDate, startTime, endTime, notes, quantity, treatmentType, helmetType, fieldConfig]);

  const buildLabel = useCallback((data: ReportFormData): string => {
    const parts: string[] = [];
    // Report type name
    const rt = availableReportTypes.find(r => r.code === data.reportTypeCode);
    if (rt) parts.push(localizedName(rt, i18n.language));
    // Hospital name
    const h = hospitals.find((h: any) => h.id === data.hospitalId);
    if (h) parts.push(localizedName(h, i18n.language));
    // Time range
    if (data.startTime && data.endTime) {
      parts.push(`${data.startTime}-${data.endTime}`);
    }
    return parts.join(' \u00B7 ') || (isHebrew ? 'דיווח' : 'Report');
  }, [availableReportTypes, hospitals, i18n.language, isHebrew]);

  const resetFormForNext = useCallback(() => {
    // Keep date and hospital, reset everything else
    setTreatmentType('');
    setHelmetType('');
    setStartTime('');
    setEndTime('');
    setNotes('');
    setQuantity(1);
    setError('');
  }, []);

  const populateFormFromData = useCallback((data: ReportFormData) => {
    setReportTypeCode(data.reportTypeCode);
    setReportDate(data.date);
    setSelectedHospitalId(data.hospitalId);
    setTreatmentType(data.treatmentType || '');
    setHelmetType(data.helmetType || '');
    setStartTime(data.startTime);
    setEndTime(data.endTime);
    setNotes(data.notes);
    setQuantity(data.quantity);
    setError('');
  }, []);

  const addToBatch = useCallback(() => {
    if (!validateForm()) return false;
    const formData = snapshotForm();
    const item: BatchItem = {
      id: crypto.randomUUID(),
      formData,
      label: buildLabel(formData),
    };
    dispatch({ type: 'ADD_ITEM', item });
    triggerHapticFeedback(15);
    resetFormForNext();
    return true;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotForm, buildLabel, resetFormForNext]);

  const startEdit = useCallback((index: number) => {
    dispatch({ type: 'START_EDIT', index });
    populateFormFromData(batch.items[index].formData);
  }, [batch.items, populateFormFromData]);

  const updateItem = useCallback(() => {
    if (!validateForm() || batch.editingIndex === null) return;
    const formData = snapshotForm();
    const item: BatchItem = {
      id: batch.items[batch.editingIndex].id,
      formData,
      label: buildLabel(formData),
    };
    dispatch({ type: 'UPDATE_ITEM', index: batch.editingIndex, item });
    triggerHapticFeedback(15);
    resetFormForNext();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotForm, buildLabel, resetFormForNext, batch.editingIndex, batch.items]);

  const cancelEdit = useCallback(() => {
    dispatch({ type: 'CANCEL_EDIT' });
    resetFormForNext();
  }, [resetFormForNext]);

  const removeItem = useCallback((index: number) => {
    dispatch({ type: 'REMOVE_ITEM', index });
    triggerHapticFeedback(15);
  }, []);

  // Check if any field beyond date/hospital is filled (to decide whether to auto-add on submit)
  const hasAnyFieldFilled = useMemo(() => {
    return !!(treatmentType || helmetType || startTime || endTime || notes.trim() || quantity > 1);
  }, [treatmentType, helmetType, startTime, endTime, notes, quantity]);

  // ============================================
  // Submit handlers
  // ============================================

  const handleSubmit = () => {
    const payload: ReportFormData = {
      reportTypeCode,
      hospitalId: selectedHospitalId,
      date: reportDate,
      startTime: isVisible('startTime') ? startTime : '',
      endTime: isVisible('endTime') ? endTime : '',
      notes: isVisible('notes') ? notes.trim() : '',
      quantity: isVisible('quantity') ? quantity : 1,
      status: 'SUBMITTED',
      ...(isVisible('treatmentType') && treatmentType ? { treatmentType } : {}),
      ...(isVisible('helmetType') && helmetType ? { helmetType } : {}),
    };

    submitWithAnimation({
      validate: validateForm,
      mutate: () =>
        isEditMode
          ? updateMutation.mutateAsync({ id: existingReport!.id, data: payload })
          : createMutation.mutateAsync(payload),
      onSuccess: () => {
        if (isEditMode) {
          toast.success(t('quickReport.updated'), { duration: 3000, position: 'top-right' });
        }
        onSuccess();
        onClose();
      },
      onError: (err: any) => {
        setError(err.response?.data?.error || t('quickReport.submitError'));
      },
    });
  };

  const handleBatchSubmit = () => {
    // If the form has valid data filled, add it as the last item
    const itemsToSubmit = [...batch.items];
    if (isFormValid && hasAnyFieldFilled) {
      const formData = snapshotForm();
      itemsToSubmit.push({
        id: crypto.randomUUID(),
        formData,
        label: buildLabel(formData),
      });
    }

    if (itemsToSubmit.length === 0) return;

    setIsSubmitting(true);

    submitBatchWithAnimation({
      items: itemsToSubmit,
      mutateOne: (formData) => createMutation.mutateAsync(formData),
      onAllDone: (succeeded, failed) => {
        setIsSubmitting(false);
        if (failed === 0) {
          toast.success(t('quickReport.batch.submitted', { count: succeeded }), { duration: 3000, position: 'top-right' });
          dispatch({ type: 'RESET' });
          onSuccess();
          onClose();
        } else {
          toast.error(t('quickReport.batch.partialError', { success: succeeded, failed }));
          // Keep modal open — user can retry
        }
      },
      onError: () => {
        setIsSubmitting(false);
        toast.error(t('quickReport.submitError'));
      },
    });
  };

  // ============================================
  // Close handler with discard confirmation
  // ============================================

  const handleClose = useCallback(() => {
    if (isBatchMode && batch.items.length > 0) {
      const confirmed = window.confirm(t('quickReport.batch.discardConfirm'));
      if (!confirmed) return;
      dispatch({ type: 'RESET' });
    }
    onClose();
  }, [isBatchMode, batch.items.length, t, onClose]);

  // Duration label for end time options
  const getTimeDurationLabel = (slot: string): string => {
    if (!startTime) return '';
    const sMins = parseInt(startTime.split(':')[0]) * 60 + parseInt(startTime.split(':')[1]);
    const eMins = parseInt(slot.split(':')[0]) * 60 + parseInt(slot.split(':')[1]);
    const durMins = eMins - sMins;
    if (durMins <= 0) return '';
    if (durMins >= 60) {
      return `${Math.floor(durMins / 60)}h${durMins % 60 ? ` ${durMins % 60}m` : ''}`;
    }
    return `${durMins}m`;
  };

  const endTimeSlots = useMemo(() => {
    if (!startTime) return [];
    const startIndex = TIME_SLOTS.indexOf(startTime);
    if (startIndex === -1) return TIME_SLOTS;
    return TIME_SLOTS.slice(startIndex + 1);
  }, [startTime]);

  const inputStyle = {
    backgroundColor: MODAL_COLORS.interactive,
    border: `1px solid ${MODAL_COLORS.border}`,
    color: MODAL_COLORS.text,
  };

  // Render the helmet selector based on compatible helmets
  const renderHelmetSelector = () => {
    if (!treatmentType) return null;

    // Single compatible helmet → auto-selected badge inline
    if (selectedTreatmentHasConnections && filteredHelmetTypes.length === 1) {
      return (
        <div className="flex items-center gap-2 px-1">
          <HardHat className="h-4 w-4" style={{ color: MODAL_COLORS.text, opacity: 0.7 }} />
          <span className="text-xs font-semibold" style={{ color: MODAL_COLORS.text }}>
            {t('quickReport.selectHelmet')}:{isRequired('helmetType') && <span style={{ color: '#EF4444' }}> *</span>}
          </span>
          <span
            id="qr-helmet"
            className="px-3 py-1 rounded-lg text-sm font-bold"
            style={{
              backgroundColor: MODAL_COLORS.header,
              color: '#fff',
              border: `1px solid ${MODAL_COLORS.border}`,
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            }}
          >
            {localizedName(filteredHelmetTypes[0], i18n.language)}
          </span>
        </div>
      );
    }

    // Multiple compatible helmets → selectable chips
    if (selectedTreatmentHasConnections && filteredHelmetTypes.length > 1) {
      return (
        <div className="space-y-1.5">
          <div className="flex items-center gap-1.5">
            <HardHat className="h-4 w-4" style={{ color: MODAL_COLORS.text, opacity: 0.7 }} />
            <span className="text-xs font-semibold" style={{ color: MODAL_COLORS.text }}>
              {t('quickReport.selectHelmet')}{isRequired('helmetType') && <span style={{ color: '#EF4444' }}> *</span>}
            </span>
          </div>
          <div id="qr-helmet" className="flex flex-wrap gap-2">
            {filteredHelmetTypes.map((ht) => {
              const isSelected = helmetType === ht.code;
              return (
                <button
                  key={ht.id}
                  type="button"
                  onClick={() => setHelmetType(ht.code)}
                  className="px-3 py-1.5 rounded-lg font-bold text-sm transition-all cursor-pointer"
                  style={{
                    backgroundColor: isSelected ? MODAL_COLORS.header : MODAL_COLORS.interactive,
                    color: isSelected ? '#fff' : MODAL_COLORS.text,
                    border: `1px solid ${MODAL_COLORS.border}`,
                    boxShadow: isSelected ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                  }}
                >
                  {localizedName(ht, i18n.language)}
                </button>
              );
            })}
          </div>
        </div>
      );
    }

    // No connections (fallback) → full dropdown
    return (
      <FormField icon={<HardHat className="h-4 w-4" />} label={t('quickReport.selectHelmet')} required={isRequired('helmetType')}>
        <select
          id="qr-helmet"
          value={helmetType}
          onChange={(e) => setHelmetType(e.target.value)}
          className="w-full p-2.5 rounded-lg font-semibold text-base appearance-none cursor-pointer focus:outline-none focus:ring-2"
          style={inputStyle}
        >
          <option value="">{isHebrew ? 'בחר סוג קסדה' : 'Select helmet type'}</option>
          {filteredHelmetTypes.map((type) => (
            <option key={type.id} value={type.code}>
              {localizedName(type, i18n.language)}
            </option>
          ))}
        </select>
      </FormField>
    );
  };

  // ============================================
  // Batch footer & title
  // ============================================

  const hasBatchItems = isBatchMode && batch.items.length > 0;

  // In create mode, ALWAYS use a custom footer:
  // - 0 items: "Add More Reports" + "Submit Report" (fork)
  // - 1+ items, editing: "Cancel" + "Update Item"
  // - 1+ items, adding: "Add Another" + "Submit N Reports"
  const footerMode = !hasBatchItems
    ? 'first' as const
    : batch.mode === 'editing'
      ? 'editing' as const
      : 'batch' as const;

  const createModeFooter = isBatchMode ? (
    <BatchFooter
      mode={footerMode}
      itemCount={hasBatchItems
        ? batch.items.length + (isFormValid && hasAnyFieldFilled ? 1 : 0)
        : 0}
      isSubmitting={isSubmitting}
      isPending={createMutation.isPending || isAnimating}
      onAddMore={addToBatch}
      onSubmitSingle={handleSubmit}
      onSubmitBatch={handleBatchSubmit}
      onUpdateItem={updateItem}
      onCancelEdit={cancelEdit}
    />
  ) : undefined;

  if (!isOpen) return null;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={handleClose}
      title={isEditMode
        ? (existingReport?.status === 'REJECTED' ? t('quickReport.resubmitRejected') : t('quickReport.editReport'))
        : t('quickReport.newReport')}
      headerStyle={{
        backgroundColor: 'rgba(255,255,255,0.55)',
        backdropFilter: 'blur(20px) saturate(180%)',
        WebkitBackdropFilter: 'blur(20px) saturate(180%)',
        color: '#1e293b',
        borderBottom: '2px solid rgba(255,255,255,0.25)',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
      }}
      isPending={createMutation.isPending || updateMutation.isPending || isSubmitting}
      isAnimating={isAnimating}
      onSubmit={isEditMode ? handleSubmit : undefined}
      onValidate={validateForm}
      isFormValid={isFormValid}
      modalContentRef={modalContentRef}
      vinylLabel="REPORT"
      error={error}
      submitLabel={isEditMode && existingReport?.status === 'REJECTED' ? t('quickReport.resubmit') : t('quickReport.submitReport')}
      customFooter={createModeFooter}
      preventClose={isSubmitting}
    >
      {/* Editing banner — above the form fields */}
      {batch.mode === 'editing' && batch.editingIndex !== null && (
        <div
          className="flex items-center justify-between p-2 rounded-lg mb-3"
          style={{
            backgroundColor: 'rgba(21,101,192,0.08)',
            border: '1px solid rgba(21,101,192,0.2)',
          }}
        >
          <span className="text-xs font-bold" style={{ color: MODAL_COLORS.text }}>
            {t('quickReport.batch.editingItem', { number: batch.editingIndex + 1 })}
          </span>
          <button
            type="button"
            onClick={cancelEdit}
            className="text-xs font-semibold px-2 py-1 rounded transition-colors hover:opacity-80"
            style={{ color: MODAL_COLORS.header }}
          >
            {t('quickReport.batch.cancelEdit')}
          </button>
        </div>
      )}

      <div className="space-y-3 pb-3">
        {/* Report Type Selector — dropdown from employment role */}
        {availableReportTypes.length > 1 && (
          <FormField icon={<FileTextIcon className="h-4 w-4" />} label={t('quickReport.reportType')} required>
            <select
              id="qr-report-type"
              value={reportTypeCode}
              onChange={(e) => setReportTypeCode(e.target.value as ReportTypeCode)}
              className="w-full p-2.5 rounded-lg font-semibold text-base appearance-none cursor-pointer focus:outline-none focus:ring-2"
              style={inputStyle}
            >
              {availableReportTypes.map((rt) => (
                <option key={rt.code} value={rt.code}>
                  {localizedName(rt, i18n.language)}
                </option>
              ))}
            </select>
          </FormField>
        )}

        {/* Date Picker */}
        <FormField icon={<CalendarIcon className="h-4 w-4" />} label={t('reports.date')} required>
          <input
            id="qr-date"
            type="date"
            value={reportDate}
            min={isEditMode ? undefined : currentMonthMin}
            max={isEditMode ? undefined : currentMonthMax}
            onClick={(e) => (e.target as HTMLInputElement).showPicker?.()}
            onChange={(e) => setReportDate(e.target.value)}
            className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2 cursor-pointer"
            style={inputStyle}
          />
        </FormField>

        {/* Hospital Selection — user's hospitals with primary first */}
        <FormField icon={<Building2Icon className="h-4 w-4" />} label={t('reports.hospital')} required>
          <select
            id="qr-hospital"
            value={selectedHospitalId}
            onChange={(e) => setSelectedHospitalId(e.target.value)}
            disabled={hospitalsLoading}
            className="w-full p-2.5 rounded-lg font-semibold text-base appearance-none cursor-pointer focus:outline-none focus:ring-2"
            style={inputStyle}
          >
            <option value="">{t('quickReport.selectHospital')}</option>
            {hospitals.map((hospital: any) => (
              <option key={hospital.id} value={hospital.id}>
                {localizedName(hospital, i18n.language)}
                {hospital.isPrimary ? ` \u2605` : ''}
              </option>
            ))}
          </select>
        </FormField>

        {/* Treatment Type — dynamic visibility */}
        {isVisible('treatmentType') && (
          <FormField icon={<FileTextIcon className="h-4 w-4" />} label={t('quickReport.selectTreatment')} required={isRequired('treatmentType')}>
            <select
              id="qr-treatment"
              value={treatmentType}
              onChange={(e) => setTreatmentType(e.target.value)}
              disabled={treatmentTypesLoading}
              className="w-full p-2.5 rounded-lg font-semibold text-base appearance-none cursor-pointer focus:outline-none focus:ring-2"
              style={inputStyle}
            >
              <option value="">{isHebrew ? 'בחר סוג טיפול' : 'Select treatment type'}</option>
              {treatmentTypes.map((type) => (
                <option key={type.id} value={type.code}>
                  {localizedName(type, i18n.language)}
                </option>
              ))}
            </select>
          </FormField>
        )}

        {/* Helmet — dynamic visibility, inline based on compatible helmets */}
        {isVisible('helmetType') && renderHelmetSelector()}

        {/* Quantity — dynamic visibility */}
        {isVisible('quantity') && (
          <FormField icon={<HashIcon className="h-4 w-4" />} label={getQuantityLabel(activeRT, i18n.language, t('quickReport.quantity'))} required={isRequired('quantity')}>
            <input
              id="qr-quantity"
              type="number"
              min={1}
              max={100}
              value={quantity}
              onChange={(e) => {
                const raw = e.target.value;
                if (raw === '') { setQuantity('' as any); return; }
                const n = parseInt(raw);
                if (!isNaN(n)) setQuantity(Math.min(100, Math.max(0, n)));
              }}
              onBlur={() => { if (!quantity || quantity < 1) setQuantity(1); }}
              className="w-full p-2.5 rounded-lg font-semibold text-base focus:outline-none focus:ring-2"
              style={inputStyle}
            />
          </FormField>
        )}

        {/* Time Selection — dynamic visibility, side by side (always LTR: start->end left-to-right) */}
        {(isVisible('startTime') || isVisible('endTime')) && (
          <div className="grid grid-cols-2 gap-2" dir="ltr">
            {isVisible('startTime') && (
              <FormField icon={<ClockIcon className="h-4 w-4" />} label={t('quickReport.selectStartTime')} required={isRequired('startTime')}>
                <select
                  id="qr-start"
                  value={startTime}
                  onChange={(e) => {
                    setStartTime(e.target.value);
                    setEndTime('');
                  }}
                  dir="ltr"
                  className="w-full p-2.5 rounded-lg font-semibold text-base appearance-none cursor-pointer focus:outline-none focus:ring-2 text-center"
                  style={inputStyle}
                >
                  <option value="">{isHebrew ? 'בחר...' : 'Select...'}</option>
                  {TIME_SLOTS.map((slot) => (
                    <option key={slot} value={slot}>{slot}</option>
                  ))}
                </select>
              </FormField>
            )}

            {isVisible('endTime') && (
              <FormField icon={<ClockIcon className="h-4 w-4" />} label={t('quickReport.selectEndTime')} required={isRequired('endTime')}>
                <select
                  id="qr-end"
                  value={endTime}
                  onChange={(e) => setEndTime(e.target.value)}
                  dir="ltr"
                  className="w-full p-2.5 rounded-lg font-semibold text-base appearance-none cursor-pointer focus:outline-none focus:ring-2 text-center"
                  style={inputStyle}
                >
                  <option value="">{isHebrew ? 'בחר...' : 'Select...'}</option>
                  {endTimeSlots.map((slot) => {
                    const durLabel = getTimeDurationLabel(slot);
                    return (
                      <option key={slot} value={slot}>
                        {slot}{durLabel ? ` (${durLabel})` : ''}
                      </option>
                    );
                  })}
                </select>
              </FormField>
            )}
          </div>
        )}

        {/* Notes — dynamic visibility */}
        {isVisible('notes') && (
          <FormField icon={<FileTextIcon className="h-4 w-4" />} label={t('quickReport.notes')} required={isRequired('notes')}>
            <input
              id="qr-notes"
              type="text"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('quickReport.notesPlaceholder')}
              maxLength={40}
              className="w-full p-2.5 rounded-lg text-base focus:outline-none focus:ring-2"
              style={inputStyle}
              dir={isRTL ? 'rtl' : 'ltr'}
            />
          </FormField>
        )}
      </div>

      {/* Batch items — compact list at the bottom */}
      {isBatchMode && batch.items.length > 0 && (
        <div
          className="sticky bottom-0 -mx-4 px-4 pt-2.5 pb-1 md:-mx-3 md:px-3"
        >
          <p
            className="text-xs font-semibold mb-1.5 px-0.5"
            style={{ color: MODAL_COLORS.text }}
          >
            {t('quickReport.batch.itemsInBatch', { count: batch.items.length })}
          </p>
          <div ref={batchListRef} className="space-y-1.5 max-h-40 overflow-y-auto">
            <AnimatePresence mode="popLayout">
              {batch.items.map((item, i) => (
                <BatchItemCard
                  key={item.id}
                  item={item}
                  index={i}
                  isEditing={batch.editingIndex === i}
                  onEdit={startEdit}
                  onRemove={removeItem}
                  language={i18n.language}
                />
              ))}
            </AnimatePresence>
          </div>
        </div>
      )}
    </FormModal>
  );
}
